/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { use, type ReactElement } from 'react';
import { Content, type BuilderContent } from '@builder.io/sdk-react';
import { GLOBAL_MODEL } from '@/lib/builder/placements';
import { BUILDER_COMPONENTS } from '@/lib/builder/registry';
import Footer from './index';

/**
 * Renders the Builder entry for the `footer` placement, falling back to the code-owned footer
 * when nothing is published or the fetch failed. Mount inside a `<Suspense>`: the entry is
 * deferred by the `_app` loader, so it never blocks first paint.
 *
 * `editing` keeps `<Content>` mounted for an unpublished entry. Its mount effect is what calls
 * `setupBrowserForEditing`, registers the custom components for this model and listens for the
 * editor's `contentUpdate` — so without it the first entry has nothing to attach to.
 */
export default function AuthoredFooter({
    entry,
    apiKey,
    editing = false,
}: Readonly<{ entry: Promise<BuilderContent | null>; apiKey: string; editing?: boolean }>): ReactElement {
    const content = use(entry);

    if (!apiKey || (!content && !editing)) return <Footer />;

    return (
        <Content
            model={GLOBAL_MODEL}
            content={content ?? undefined}
            apiKey={apiKey}
            customComponents={BUILDER_COMPONENTS}
        />
    );
}
